import React, { useState, useEffect } from 'react';
import {
  Card, Input, Button, message, Spin, Tag, Divider, Typography, Space, Row, Col,
  Switch, Select,
} from 'antd';
import {
  WhatsAppOutlined, MessageOutlined, MailOutlined, PhoneOutlined,
  SaveOutlined, EyeInvisibleOutlined, EyeTwoTone, MobileOutlined,
} from '@ant-design/icons';
import axios from 'axios';
import { baseUrl } from '../../config';
import { getSettings, updateSetting } from './service';

const { Title, Text } = Typography;
const { Option } = Select;

const CATEGORY_META = {
  app:      { label: 'BrahmSetu App — Version Control', icon: <MobileOutlined />,    color: '#7a0000' },
  whatsapp: { label: 'WhatsApp (AiSensy)',               icon: <WhatsAppOutlined />, color: '#25D366' },
  sms:      { label: 'SMS (Aarvi Telecom)',               icon: <MessageOutlined />,  color: '#1890ff' },
  email:    { label: 'Email / SMTP',                     icon: <MailOutlined />,     color: '#fa8c16' },
  voice:    { label: 'Voice Call (Aarvi Telecom)',       icon: <PhoneOutlined />,    color: '#722ed1' },
};

const PASSWORD_KEYS     = ['smtp_password', 'whatsapp_api_key', 'sms_api_key', 'aarvi_token'];
const TOGGLE_KEYS       = ['voice_call_on_payment'];
const ANNOUNCEMENT_KEYS = ['voice_call_payment_announcement_id'];
const TEXTAREA_KEYS     = ['app_update_message'];

export default function Settings() {
  const [loading, setLoading]   = useState(true);
  const [settings, setSettings] = useState({});
  const [labels, setLabels]     = useState({});
  const [order, setOrder]       = useState({});
  const [saving, setSaving]     = useState({});
  const [edited, setEdited]     = useState({});

  // Announcements for the dropdown
  const [announcements, setAnnouncements] = useState([]);

  useEffect(() => {
    load();
    loadAnnouncements();
  }, []);

  async function load() {
    setLoading(true);
    try {
      const data = await getSettings();
      const vals = {};
      const lbls = {};
      const ord  = {};
      (data.result || []).forEach(row => {
        vals[row.setting_key] = row.setting_value || '';
        lbls[row.setting_key] = row.label || row.setting_key;
        if (!ord[row.category]) ord[row.category] = [];
        ord[row.category].push(row.setting_key);
      });
      setSettings(vals);
      setLabels(lbls);
      setOrder(ord);
    } catch (err) {
      message.error('Failed to load settings.');
    } finally {
      setLoading(false);
    }
  }

  async function loadAnnouncements() {
    try {
      const resp = await axios.get(`${baseUrl}/voice-call/announcement-list`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('accessToken')}` },
      });
      if (resp.data.status) setAnnouncements(resp.data.result || []);
    } catch (_) {}
  }

  function handleChange(key, val) {
    setEdited(prev => ({ ...prev, [key]: val }));
  }

  async function handleSave(key) {
    const val = edited[key] !== undefined ? edited[key] : settings[key];
    setSaving(prev => ({ ...prev, [key]: true }));
    try {
      await updateSetting(key, val);
      setSettings(prev => ({ ...prev, [key]: val }));
      setEdited(prev => { const n = { ...prev }; delete n[key]; return n; });
      message.success('Saved.');
    } catch (err) {
      message.error('Failed to save.');
    } finally {
      setSaving(prev => ({ ...prev, [key]: false }));
    }
  }

  function currentVal(key) {
    return edited[key] !== undefined ? edited[key] : (settings[key] || '');
  }

  function isDirty(key) {
    return edited[key] !== undefined && edited[key] !== settings[key];
  }

  // Render the appropriate input for a setting key
  function renderInput(key) {
    const val = currentVal(key);

    // yes/no toggle
    if (TOGGLE_KEYS.includes(key)) {
      const isOn = val === 'yes';
      return (
        <Space>
          <Switch
            checked={isOn}
            checkedChildren="ON"
            unCheckedChildren="OFF"
            onChange={checked => handleChange(key, checked ? 'yes' : 'no')}
          />
          <Tag color={isOn ? 'green' : 'default'} style={{ fontWeight: 700 }}>
            {isOn ? 'Voice call will be made on every payment' : 'Disabled — no auto call'}
          </Tag>
        </Space>
      );
    }

    // Announcement dropdown
    if (ANNOUNCEMENT_KEYS.includes(key)) {
      return (
        <Select
          showSearch
          allowClear
          value={val || undefined}
          onChange={v => handleChange(key, v || '')}
          placeholder="Select announcement — add one in Calling Module first"
          style={{ width: '100%' }}
          optionFilterProp="label"
          notFoundContent={
            <div style={{ textAlign: 'center', padding: 8, color: '#888', fontSize: 12 }}>
              No announcements yet — go to Calling Module → Register by ID
            </div>
          }
        >
          {announcements.map(a => (
            <Option
              key={a.announcement_id}
              value={String(a.announcement_id)}
              label={`${a.announcement_name || a.announcement_id} (${a.announcement_id})`}
            >
              <span style={{ fontWeight: 600 }}>{a.announcement_name || `Announcement ${a.announcement_id}`}</span>
              <span style={{ color: '#888', fontSize: 11, marginLeft: 8 }}>ID: {a.announcement_id}</span>
            </Option>
          ))}
        </Select>
      );
    }

    // Textarea
    if (TEXTAREA_KEYS.includes(key)) {
      return (
        <Input.TextArea
          value={val}
          onChange={e => handleChange(key, e.target.value)}
          rows={2}
          style={{ fontSize: 13 }}
        />
      );
    }

    // Password
    if (PASSWORD_KEYS.includes(key)) {
      return (
        <Input.Password
          value={val}
          onChange={e => handleChange(key, e.target.value)}
          iconRender={visible => visible ? <EyeTwoTone /> : <EyeInvisibleOutlined />}
          style={{ fontFamily: 'monospace', fontSize: 12 }}
        />
      );
    }

    // Plain text
    return (
      <Input
        value={val}
        onChange={e => handleChange(key, e.target.value)}
        style={{ fontFamily: 'monospace', fontSize: 12 }}
      />
    );
  }

  if (loading) {
    return <div style={{ textAlign: 'center', padding: 60 }}><Spin size="large" /></div>;
  }

  return (
    <div style={{ maxWidth: 860, margin: '0 auto', padding: '24px 16px' }}>
      <Title level={3} style={{ marginBottom: 4 }}>API Settings</Title>
      <Text type="secondary" style={{ fontSize: 13 }}>
        Manage third-party API credentials and automation toggles. Changes take effect within 5 minutes (cached).
      </Text>

      {Object.keys(CATEGORY_META).map(category => {
        const keys = order[category] || [];
        if (!keys.length) return null;
        const meta = CATEGORY_META[category];

        return (
          <Card
            key={category}
            style={{ marginTop: 24, borderRadius: 10 }}
            title={
              <Space>
                <span style={{ color: meta.color, fontSize: 18 }}>{meta.icon}</span>
                <span style={{ fontWeight: 700 }}>{meta.label}</span>
              </Space>
            }
          >
            {keys.map((key, idx) => {
              const dirty = isDirty(key);

              return (
                <div key={key}>
                  {idx > 0 && <Divider style={{ margin: '12px 0' }} />}
                  <Row align="middle" gutter={[12, 8]}>
                    <Col span={24}>
                      <Text strong style={{ fontSize: 13 }}>
                        {labels[key]}
                        {dirty && (
                          <Tag color="orange" style={{ marginLeft: 8, fontSize: 11 }}>unsaved</Tag>
                        )}
                      </Text>
                    </Col>
                    <Col flex="1">
                      {renderInput(key)}
                    </Col>
                    <Col>
                      <Button
                        type={dirty ? 'primary' : 'default'}
                        icon={<SaveOutlined />}
                        loading={saving[key]}
                        onClick={() => handleSave(key)}
                        disabled={!dirty}
                      >
                        Save
                      </Button>
                    </Col>
                  </Row>
                </div>
              );
            })}
          </Card>
        );
      })}
    </div>
  );
}
